import React, { Fragment, useState } from 'react';
import styled from 'styled-components';
import shortid from 'shortid';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { pluralize, translate } from 'utils/helpers';
import {
    ButtonBigDashed, FlexContainer, FontIcon, ItemOption, ItemSelected, Text, TextMultiline
} from 'components';
import { useModal } from 'hooks';
import { observer } from 'mobx-react-lite';
import { LeakEntryView } from 'models';
import { Controller } from 'react-hook-form';
import { Question } from '../Question/Question';

const LeakItemContainer = styled.div`
  margin-bottom: 1rem;
`;

interface FormStateLeaksView {
    leakFound?: boolean;
    leaks?: LeakEntryView[];
}

export const Leaks = observer(({
                                   control,
                                   setValue,
                                   watch
                               }: {
    control: any;
    setValue: any;
    watch: any;
}) => {
    const [, forceUpdate] = useState({});

    const { open, close } = useModal();

    const leakFound = watch('leakFound');
    const leaks = watch('leaks') || [];

    const getNextIndex = () => {
        let index = 1;
        // eslint-disable-next-line no-loop-func
        while (leaks.find(l => l.index === index)) {
            index++;
        }
        return index;
    };

    const handleForm = (form: LeakEntryView, create: boolean) => {
        const currentLeaks = [...leaks];

        if (create) {
            // Get the next available index and add the leak to the list
            const index = getNextIndex();
            currentLeaks.push({
                ...form,
                index
            });
        } else {
            // Update the indexed leak in the list
            const existingLeakIndex = currentLeaks.findIndex(leak => leak.index === form.index);
            currentLeaks[existingLeakIndex] = form;
        }

        setValue('leaks', currentLeaks, { shouldValidate: true });

        forceUpdate({});

        close();
    };

    const removeLeak = (index: number) => {
        // Remove the leak with the matching index
        const currentLeaks = leaks.filter(leak => leak.index !== index);
        setValue('leaks', currentLeaks, { shouldValidate: true });
        (currentLeaks.length === 0) && setValue('leakFound', false, { shouldValidate: true });

        forceUpdate({});
    };

    const resetLeaks = () => {
        const messageSingle = translate('interventionForm.leaks.resetLeakSingle');
        const messageMulti = translate('interventionForm.leaks.resetLeakMulti');

        open({
            type: 'WARNING',
            text: leaks.length > 1 ? messageMulti : messageSingle,
            buttonConfirm: translate('button.accept'),
            buttonCancel: translate('button.cancel'),
            onConfirm: () => {
                setValue('leaks', [], { shouldValidate: true });
                forceUpdate({});
            },
            onCancel: () => setValue('leakFound', true, { shouldValidate: true })
        });
    };

    return (
      <Question label={translate('interventionForm.leaks.questionSawLeak')}>
        <Controller
                control={control}
                name="leakFound"
                render={({ field }) => (
                  <ToggleButtonGroup
                        aria-label={translate('interventionForm.leaks.questionSawLeak')}
                        exclusive
                        size="medium"
                        value={field.value}
                        onChange={(event, value) => {
                            if (value === null) return;
                            setValue('leakFound', value, { shouldValidate: true });
                            forceUpdate({});
                            value === false && leaks.length > 0 && resetLeaks();
                        }}
                    >
                    <ToggleButton
                            aria-label={translate('common.yes')}
                            data-cy="foundLeaks"
                            value={true}
                            onClick={() => open({
                                type: 'ADD_LEAK',
                                onSubmit: (form: LeakEntryView) => handleForm(form, true),
                                onClose: leaks.length === 0 && (() => {
                                    setValue('leakFound', false, { shouldValidate: true });
                                    forceUpdate({});
                                })
                            })}
                        >
                      {translate('common.yes')}
                    </ToggleButton>
                    <ToggleButton
                            aria-label={translate('common.no')}
                            value={false}
                        >
                      {translate('common.no')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
            />

        {leaks && leaks.length > 0 && (
        <Fragment>
          <Text fontWeight={600}>
            {translate('interventionForm.leaks.allLeakAdded', {
                            total: leaks.length,
                            leaks: pluralize('fuite', leaks.length),
                            added: pluralize('constatée', leaks.length)
                        })}
          </Text>
          {leaks.map((leak, index) => (
            <LeakItemContainer data-cy={`leakAdded${index}`} key={shortid.generate()}>
              <ItemSelected
                                deleteAction={() => removeLeak(leak.index)}
                                editAction={() => open({
                                    type: 'ADD_LEAK',
                                    onSubmit: (form: LeakEntryView) => handleForm(form, false),
                                    onClose: leaks.length === 0 && (() => {
                                        setValue('leakFound', false, { shouldValidate: true });
                                        // Forcer un rendu immédiat
                                        forceUpdate({});
                                    }),
                                    currentLeak: leak
                                })}
                            >
                <ItemOption>
                  <FlexContainer alignItems="center" flexDirection="column">
                    <FontIcon
                                            color={leak.leakRepaired ? 'var(--green)' : 'var(--red)'}
                                            fontSize="4rem"
                                            icon="icon-hazard"
                                        />
                    <Text color={leak.leakRepaired ? 'var(--green)' : 'var(--red)'} textTransform="uppercase">
                      {`${leak.leakRepaired ? translate('common.done') : translate('common.todo')}`}
                    </Text>
                  </FlexContainer>
                  <article>
                    <TextMultiline>{leak.location}</TextMultiline>
                  </article>
                  {leak.documents && leak.documents.length > 0 && (
                  <FlexContainer alignItems="center">
                    {leak.documents.map(picture => (
                      <img
                                                    alt={picture.name}
                                                    key={shortid.generate()}
                                                    src={picture.base64Content}
                                                    onClick={() => open({
                                                        ...picture,
                                                        type: 'DISPLAY_PICTURE'
                                                    })}
                                                />
                                            ))}
                  </FlexContainer>
                                    )}
                </ItemOption>
              </ItemSelected>
            </LeakItemContainer>
                    ))}
        </Fragment>
            )}

        {leakFound && leaks.length < 3 && (
        <p>
          <ButtonBigDashed onClick={() => open({
                        type: 'ADD_LEAK',
                        onSubmit: (form: LeakEntryView) => handleForm(form, true),
                        onClose: leaks.length === 0 && (() => {
                            setValue('leakFound', false, { shouldValidate: true });
                            forceUpdate({});
                        })
                    })}>
            <FontAwesomeIcon icon={faPlus} />
            {translate('interventionForm.leaks.addNewLeak')}
          </ButtonBigDashed>
        </p>
            )}
      </Question>
    );
});
